/** Kleine geteilte Bausteine der Oberfläche. */
import type { ReactNode } from 'react';
import { locale, type Key } from './i18n.js';

/**
 * Avatar aus den Initialen. Die Farbe wird aus dem Namen abgeleitet, damit
 * dieselbe Person immer dieselbe Farbe hat — ohne dass irgendwo eine Zuordnung
 * gespeichert werden müsste.
 */
/**
 * Avatarfarben aus dem Entwurf (Main-html/Main.dc.html).
 *
 * Eine feste Palette statt eines berechneten HSL-Werts: Ein freier Farbkreis
 * trifft zwangslaeufig auch Toene, die neben der Akzentfarbe unruhig wirken
 * oder auf dunklem Grund zu dunkel geraten. Diese acht sind aufeinander
 * abgestimmt und alle hell genug fuer die dunkle Schrift darauf.
 */
const AVATAR_COLORS = [
  '#6C6FF0',
  '#34B7A7',
  '#E0A63E',
  '#FF8B6B',
  '#E0729C',
  '#5B8DEF',
  '#3FBF9F',
  '#A78BFA',
];

export function Avatar({ name, small = false }: { name: string; small?: boolean }) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;

  const initials = name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (
    <span
      className={small ? 'avatar avatar-sm' : 'avatar'}
      style={{ background: AVATAR_COLORS[hash % AVATAR_COLORS.length] }}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

/* Symbole inline, weil externe Assets an der CSP scheitern. */

export function LockMark() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 10V7a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <rect x="4" y="10" width="16" height="11" rx="3" fill="currentColor" />
    </svg>
  );
}

/**
 * Symbole aus dem Entwurf (Main-html/Main.dc.html).
 *
 * Als SVG mit `currentColor` statt als Emoji: Emoji werden von jedem
 * Betriebssystem anders gezeichnet — auf Android bunt und rund, auf Windows
 * flach, auf iOS wieder anders. Eine Oberflaeche, deren Bedienelemente je
 * nach Geraet unterschiedlich aussehen und unterschiedlich gross ausfallen,
 * laesst sich nicht gestalten. Ausserdem erben SVG-Symbole die Textfarbe und
 * damit den Zustand des Knopfs.
 */
function Icon({ children, size = 17 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function SendIcon() {
  return (
    <Icon size={17}>
      <path d="M22 2 11 13" strokeWidth="2" />
      <path d="M22 2 15 22l-4-9-9-4 20-7Z" strokeWidth="2" />
    </Icon>
  );
}

export function PhoneIcon() {
  return (
    <Icon>
      <path d="M6 3h3l2 5-2.5 2a12 12 0 0 0 5.5 5.5l2-2.5 5 2v3a2 2 0 0 1-2 2c-8 0-15-7-15-15a2 2 0 0 1 2-2Z" />
    </Icon>
  );
}

export function VideoIcon() {
  return (
    <Icon>
      <rect x="3" y="6" width="12" height="12" rx="2.5" />
      <path d="M15 10l6-3v10l-6-3" />
    </Icon>
  );
}

export function SettingsIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v3M12 18.5v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M1.5 12h3M19.5 12h3M4.9 19.1l2.1-2.1M17 7l2.1-2.1" />
    </Icon>
  );
}

export function MicIcon({ off = false }: { off?: boolean }) {
  return (
    <Icon>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
      {/* Der Querstrich zeigt "aus" auch ohne Farbe — eine rote Flaeche allein
          waere fuer Rot-Gruen-Schwaeche kein Unterschied. */}
      {off && <path d="M4 3l16 18" />}
    </Icon>
  );
}

export function SwitchCameraIcon() {
  return (
    <Icon>
      <path d="M3 12a9 9 0 0 1 9-9c2.6 0 4.9 1.1 6.5 2.9M21 12a9 9 0 0 1-9 9c-2.6 0-4.9-1.1-6.5-2.9" />
      <path d="M18 2v4h-4M6 22v-4h4" />
    </Icon>
  );
}

export function FullscreenIcon({ exit = false }: { exit?: boolean }) {
  return (
    <Icon>
      {exit ? (
        <path d="M9 3v6H3M15 21v-6h6M3 15h6v6M21 9h-6V3" />
      ) : (
        <path d="M3 9V3h6M21 15v6h-6M3 15v6h6M21 9V3h-6" />
      )}
    </Icon>
  );
}

export function HangUpIcon() {
  return (
    <Icon size={19}>
      <path d="M2.5 15.5c-.6-.6-.6-1.6 0-2.2C5 10.8 8.4 9.5 12 9.5s7 1.3 9.5 3.8c.6.6.6 1.6 0 2.2l-1.8 1.8a1.3 1.3 0 0 1-1.8 0l-1.6-1.6a1.4 1.4 0 0 1-.4-1.2l.2-1.3A11 11 0 0 0 12 12.5c-1.4 0-2.8.25-4.1.7l.2 1.3c.07.44-.08.88-.4 1.2l-1.6 1.6a1.3 1.3 0 0 1-1.8 0Z" />
    </Icon>
  );
}

export function AcceptCallIcon() {
  return (
    <Icon size={19}>
      <path d="M6 3h3l2 5-2.5 2a12 12 0 0 0 5.5 5.5l2-2.5 5 2v3a2 2 0 0 1-2 2c-8 0-15-7-15-15a2 2 0 0 1 2-2Z" />
    </Icon>
  );
}

export function CheckIcon({ double = false }: { double?: boolean }) {
  return (
    <svg
      width={double ? 16 : 11}
      height="11"
      viewBox={double ? '0 0 22 16' : '0 0 16 16'}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 8.5 5 12.5 12.5 3.5" />
      {double && <path d="M9 12.5 16.5 3.5" />}
    </svg>
  );
}

export function CameraIcon({ off = false }: { off?: boolean }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="6" width="12" height="12" rx="2.5" />
      <path d="M15 10l6-3v10l-6-3" />
      {/* Der Querstrich zeigt "aus" auch ohne Farbe — bei Rot-Gruen-Schwaeche
          waere ein Farbwechsel allein kein Unterschied. */}
      {off && <path d="M3 3l18 18" />}
    </svg>
  );
}

export function ShieldIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2.5 4.5 5.5v6c0 4.4 3 8.4 7.5 10 4.5-1.6 7.5-5.6 7.5-10v-6Z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

export function CopyIcon() {
  return (
    <Icon>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h8" />
    </Icon>
  );
}

export function CloseIcon() {
  return (
    <Icon size={20}>
      <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" />
    </Icon>
  );
}

export function BackIcon() {
  return (
    <Icon size={20}>
      <path d="M15 5l-7 7 7 7" strokeWidth="2" />
    </Icon>
  );
}

export function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(locale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Day separator in the conversation.
 *
 * "Today" and "Yesterday" are translated; everything older is left to
 * `toLocaleDateString`, which knows each language's date order — the reason
 * not to assemble the date by hand.
 */
export function formatDay(timestamp: number, t: (key: Key) => string): string {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return t('today');
  if (sameDay(date, yesterday)) return t('yesterday');

  return date.toLocaleDateString(locale(), {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/** Short form for the chat list: time today, date otherwise. */
export function formatListTime(timestamp: number): string {
  const date = new Date(timestamp);
  if (date.toDateString() === new Date().toDateString()) return formatTime(timestamp);
  return date.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit' });
}
